import {computed, ref, watch} from 'vue';
import {useAbortableUniversalFetch, useLogger} from '#imports';

interface AutocompleteResponse {
    suggestions: string[];
}

export function useQueryAutocomplete() {
    const logger = useLogger();
    const QUERY_MAX_LENGTH = 100;

    const {execute: fetchAndMaybeAbort} = useAbortableUniversalFetch();

    const query = ref<string | null>('');
    const querySuggestions = ref<string[]>([]);
    const queryRef = ref();
    const loading = ref(false);

    const sanitizedQuery = computed(() => {
        const queryParams = new URLSearchParams();
        if (!query.value) {
            queryParams.set('q', '');
            return queryParams;
        }
        const q = query.value.trim().slice(0, QUERY_MAX_LENGTH);
        queryParams.set('q', q);
        return queryParams;
    });

    watch(query, () => {
        if (!query.value || query.value.length <= 1) {
            querySuggestions.value = [];
        }
    });

    function onQueryTab($event: KeyboardEvent) {
        if (queryRef.value.overlayVisible) {
            $event.preventDefault();
        }
    }

    async function querySearch() {
        loading.value = true;

        if (!query.value || query.value.length <= 1) {
            // Prevent the loader to be visible when it's not wanted (see: https://github.com/primefaces/primevue/issues/5601#issuecomment-2076067073)
            querySuggestions.value = [];
            loading.value = false;
            return;
        }

        try {
            const response = await fetchAndMaybeAbort<AutocompleteResponse>(`/search/api/search/autocomplete?${sanitizedQuery.value}`, {
                baseURL: '/',
            });
            if (response) {
                querySuggestions.value = response.suggestions;
            }
        } catch (error) {
            logger.error(error);
        } finally {
            loading.value = false;
        }
    }

    function clearQuery() {
        query.value = '';
    }

    return {
        query,
        sanitizedQuery,
        queryRef,
        querySuggestions,
        onQueryTab,
        querySearch,
        clearQuery,
        loading,
    };
}
